import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, Lock } from 'lucide-react';
import { customerApi } from '../../lib/api';

/**
 * Customer self-service profile.
 * Read-only: email · name · company (locked at signup, must contact support to change)
 * Editable: phone, address, city, state, zip
 */
export default function Profile({ me, refreshMe }) {
  const [form, setForm] = useState({ phone: '', address: '', city: '', state: '', zip_code: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (me) {
      setForm({
        phone: me.phone || '',
        address: me.address || '',
        city: me.city || '',
        state: me.state || '',
        zip_code: me.zip_code || '',
      });
    }
  }, [me]);

  const upd = (k, v) => setForm({ ...form, [k]: v });

  const submit = async () => {
    const changes = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v !== (me?.[k] || '')) changes[k] = v;
    });
    if (Object.keys(changes).length === 0) return toast.info('No changes to save');
    setSaving(true);
    try {
      await customerApi.patch('/customer/profile', { changes });
      toast.success('Profile saved');
      if (refreshMe) refreshMe();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fade-in space-y-6 max-w-2xl" data-testid="profile-page">
      <div>
        <h1 className="text-3xl font-semibold">My Profile</h1>
        <p className="text-sm text-[#6b7280] mt-1">Update your contact info and shipping address.</p>
      </div>

      {/* Read-only identity card */}
      <div className="card p-5 space-y-3 bg-[#fafafa]" data-testid="profile-identity">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#6b7280] font-semibold">
          <Lock size={12} /> Account Identity (locked)
        </div>
        <RO label="Email" value={me?.email || ''} />
        <RO label="Name" value={me?.name || ''} />
        <RO label="Company" value={me?.company || ''} />
        <p className="text-[11px] text-[#9ca3af] italic mt-1">
          To change these, please <a href="mailto:billing@ottomed.org" className="text-[#d92027] underline">contact OttoMed support</a>.
        </p>
      </div>

      {/* Editable section */}
      <div className="card p-5 space-y-3" data-testid="profile-edit">
        <div className="text-[11px] uppercase tracking-wider text-[#6b7280] font-semibold mb-1">
          Contact &amp; Shipping
        </div>
        <F label="Phone">
          <input
            className="inp"
            value={form.phone}
            onChange={(e) => upd('phone', e.target.value)}
            data-testid="prof-phone"
          />
        </F>
        <F label="Address">
          <input
            className="inp"
            value={form.address}
            onChange={(e) => upd('address', e.target.value)}
            data-testid="prof-address"
          />
        </F>
        <div className="grid grid-cols-3 gap-3">
          <F label="City">
            <input className="inp" value={form.city} onChange={(e) => upd('city', e.target.value)} />
          </F>
          <F label="State">
            <input className="inp" value={form.state} onChange={(e) => upd('state', e.target.value)} />
          </F>
          <F label="Zip">
            <input className="inp" value={form.zip_code} onChange={(e) => upd('zip_code', e.target.value)} />
          </F>
        </div>
        <button
          onClick={submit}
          disabled={saving}
          className="btn-brand flex items-center gap-2 mt-2 disabled:opacity-50"
          data-testid="submit-profile-btn"
        >
          <Save size={14} /> {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>

      <style>{`
        .inp{width:100%;padding:9px 12px;border:1px solid #e7e7e7;border-radius:8px;font-size:14px;}
        .inp:focus{outline:none;border-color:#d92027;}
      `}</style>
    </div>
  );
}

function F({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-[#1f1f1f] uppercase tracking-wide mb-1">{label}</div>
      {children}
    </label>
  );
}

function RO({ label, value }) {
  return (
    <div>
      <div className="text-xs font-semibold text-[#9ca3af] uppercase tracking-wide mb-0.5">{label}</div>
      <div className="text-sm text-[#0a0a0a] font-medium">{value || '—'}</div>
    </div>
  );
}
